import React from 'react';
import { getMediaUrl } from '../../lib/media';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Renders a date-bucketed timeline grid. `groups` is the shape returned by
 * `GET /collections/:id/uploads` — `[{date, uploads}]`. Read-only by design.
 *
 * `forwardCounts` is an owner-only map { upload_id: count } powering the
 * 🔥 chip on tiles whose moments have actually spread (≥3 forwards).
 * Parents receive `{}` from the API so the chip never renders for them.
 */
const TimelineGrid = ({ groups, forwardCounts = {} }) => {
  if (!groups || groups.length === 0) {
    return (
      <div
        className="rounded-2xl p-8 text-center text-sm text-gray-500"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px dashed rgba(255,255,255,0.08)',
        }}
        data-testid="timeline-empty"
      >
        Nothing here yet.
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="timeline-grid">
      {groups.map((group) => (
        <section key={group.date} data-testid={`timeline-bucket-${group.date}`}>
          <h2 className="text-[11px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-3">
            {group.date}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {group.uploads.map((u) => {
              const isVideo = u.media_type === 'video';
              const thumbUrl = isVideo
                ? getMediaUrl(u.storage_path, null)
                : getMediaUrl(u, { size: 'thumb_800' });
              const fullUrl = getMediaUrl(u.storage_path, null);
              const url = (thumbUrl || fullUrl) || `${API}/media/${u.upload_id}`;
              const linkUrl = fullUrl || url;
              // Forward chip — owner-only signal. Hidden under 3 (per spec —
              // never show weak numbers). 3-5 → 🔥 (signal). 6+ → 🔥 N (count).
              const forwardN = forwardCounts[u.upload_id] || 0;
              const showChip = forwardN >= 3;
              const chipLabel = forwardN >= 6 ? `🔥 ${forwardN}` : '🔥';
              return (
                <a
                  key={u.upload_id}
                  href={linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative aspect-square overflow-hidden rounded-lg bg-black/40 group"
                  data-testid={`timeline-item-${u.upload_id}`}
                >
                  {isVideo ? (
                    <video
                      src={url}
                      muted
                      playsInline
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <img
                      src={url}
                      alt={u.original_filename || ''}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  )}
                  {isVideo && (
                    <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-black/70 text-white tracking-wide">
                      VIDEO
                    </span>
                  )}
                  {showChip && (
                    <span
                      className="absolute top-1.5 right-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold text-white"
                      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }}
                      data-testid={`timeline-forward-chip-${u.upload_id}`}
                    >
                      {chipLabel}
                    </span>
                  )}
                </a>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default TimelineGrid;
